// sensei-app/client/ui/ClassroomTranscript.js
import { ComponentBase } from "../../core/ComponentBase.js";
import { marked } from "https://cdn.jsdelivr.net/npm/marked/lib/marked.esm.js";

export class ClassroomTranscript extends ComponentBase {
    static styles = `
        /* ... existing styles are correct, no changes needed here ... */
          .classroom-transcript {
            border-bottom: 1px solid var(--bs-border-color);
            display: flex;
            flex-direction: column;
            background-color: var(--bs-tertiary-bg);
            flex-shrink: 0;
            transition: height 0.3s ease-in-out, opacity 0.3s ease-in-out;
            height: 40%; /* Default open height */
        }
        .classroom-transcript.collapsed {
            height: 45px; /* Height of just the header */
            overflow: hidden;
        }
        .transcript-header {
            padding: 0.5rem 1rem;
            font-weight: 500;
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex-shrink: 0;
            height: 45px;
            cursor: pointer;
        }
        .transcript-body {
            flex-grow: 1;
            overflow-y: auto;
            padding: 1rem;
        }
        .transcript-entry { margin-bottom: 1rem; }
        .transcript-entry .sender { font-weight: bold; font-size: 0.9rem; margin-bottom: 0.25rem; }
        .transcript-entry .content { font-size: 0.95rem; white-space: pre-wrap; }
        .transcript-entry .content img { max-width: 100%; border-radius: var(--bs-border-radius); border: 1px solid var(--bs-border-color); }
    `;

    constructor(config) {
        super(config);
        this.messages = [];
        this.isCollapsed = false;
        this.streamingMessageElement = null;
        this.streamingMessageBuffer = ""; // NEW: The raw text buffer for the current streaming message
        this.applyStyles();
    }



    render() {
        if (!this.container) {
            this.container = document.createElement('div');
        }
        this.container.className = 'classroom-transcript';
        this.container.classList.toggle('collapsed', this.isCollapsed);
        const iconClass = this.isCollapsed ? 'bi-chevron-up' : 'bi-chevron-down';

        this.container.innerHTML = `
            <div class="transcript-header" id="transcript-header-toggle">
                <span>${this.t('Session Transcript')}</span>
                <button class="btn btn-sm btn-link text-secondary" title="${this.t('Toggle Transcript')}">
                    <i class="bi ${iconClass}"></i>
                </button>
            </div>
            <div class="transcript-body"></div>
        `;

        this.container.querySelector('#transcript-header-toggle').addEventListener('click', () => {
            this.emit('toggleVisibility');
        });

        const body = this.container.querySelector('.transcript-body');
        this.messages.forEach(msg => this.renderSingleMessage(msg, body));

        this.appClient.tx.applyTranslations(this.container);
        return this.container;
    }

    addMessage(messageObject) {
        this.messages.push(messageObject);
        const body = this.container.querySelector('.transcript-body');
        if (body) {
            this.renderSingleMessage(messageObject, body);
            // Don't auto-scroll if it's an error, let user see it
            if (messageObject.sender !== 'error') {
                body.scrollTop = body.scrollHeight;
            }
        }
    }

    streamMessageChunk(chunk) {
        const body = this.container.querySelector('.transcript-body');
        if (!body) return;

        if (!this.streamingMessageElement) {
            // This is the start of a new message stream.
            // Create a new message entry for the stream to start.
            const entry = this.renderSingleMessage({ sender: 'ai', content: '' }, body);
            // Store the content div of the newly created entry.
            this.streamingMessageElement = entry.querySelector('.content');
            // IMPORTANT: Reset the raw text buffer.
            this.streamingMessageBuffer = "";
        }

        if (this.streamingMessageElement) {
            // 1. Append the raw chunk to our internal buffer.
            this.streamingMessageBuffer += chunk;

            // 2. Parse the *entire* raw buffer and update the bubble's HTML.
            this.streamingMessageElement.innerHTML = marked.parse(this.streamingMessageBuffer);

            // 3. Auto-scroll.
            body.scrollTop = body.scrollHeight;
        }
    }


    finalizeStreamedMessage() {
        const fullText = this.streamingMessageBuffer; // Get the final text from the buffer

        // Reset state for the next message
        this.streamingMessageElement = null;
        this.streamingMessageBuffer = "";

        return fullText; // Return the full, raw text content for the parent view to inspect.
    }


    getStreamingBuffer() {
        // This now correctly returns the content from our internal raw text buffer.
        return this.streamingMessageBuffer;
    }


    // NEW METHOD: Add this to ClassroomTranscript.js
    updateLastMessageContent(newContent) {
        // Helper method to update the final content of the last message bubble after processing.
        const lastBubbleContent = this.container.querySelector('.transcript-entry:last-child .content');
        if (lastBubbleContent) {
            lastBubbleContent.innerHTML = marked.parse(newContent);
        }
    }



    renderSingleMessage(messageObject, parentElement) {
        const entry = document.createElement('div');
        entry.className = 'transcript-entry';

        const sender = document.createElement('div');
        sender.className = 'sender';
        const senderText = messageObject.sender === 'ai' ? 'Tutor' : (messageObject.sender === 'error' ? 'System Error' : 'You');
        sender.textContent = this.t(senderText);
        if (messageObject.sender === 'error') sender.classList.add('text-danger');

        const content = document.createElement('div');
        content.className = 'content';

        if (messageObject.content) {
            content.innerHTML = marked.parse(messageObject.content);
        }

        if (messageObject.inputThumbnail) {
            const imgNode = document.createElement('img');
            imgNode.src = messageObject.inputThumbnail;
            imgNode.alt = "Student drawing";
            imgNode.style.marginTop = messageObject.content ? '0.5rem' : '0';
            content.appendChild(imgNode);
        }

        entry.appendChild(sender);
        entry.appendChild(content);
        parentElement.appendChild(entry);
        return entry; // Return the created entry element
    }



    clear() {
        this.messages = [];
        const body = this.container.querySelector('.transcript-body');
        if (body) body.innerHTML = '';
    }
}